import { fetchComposer } from '@nf/utils-common/compose-fetch';
import JSEncrypt from 'jsencrypt';

type LoginPublicKeyResponse = {
	ErrorCode: number;
	ErrorMsg?: string;
	Data?: {
		PublicKey?: string;
	};
};

export const getLoginPublicKey = async (apiBackendUrl: string): Promise<string> => {
	if (!apiBackendUrl) {
		throw new Error('API backend URL is unavailable.');
	}

	const response = await fetchComposer.get(`${apiBackendUrl}/Athena/GetLoginPublicKey`);

	if (!response.ok) {
		throw new Error(
			`Failed to get login public key. Status: ${response.status}`
		);
	}

	const data = await response.json() as LoginPublicKeyResponse;

	if (data.ErrorCode !== 0) {
		throw new Error(
			`Failed to get login public key. ErrorCode: ${data.ErrorCode}`
		);
	}

	const publicKey = data.Data?.PublicKey?.trim();

	if (!publicKey) {
		throw new Error('Login public key is unavailable.');
	}

	return publicKey;
};

const convertPublicKeyToPem = (publicKey: string): string => {
	const normalizedKey = publicKey
		.replace('-----BEGIN PUBLIC KEY-----', '')
		.replace('-----END PUBLIC KEY-----', '')
		.replace(/\s+/g, '');
	const lines = normalizedKey.match(/.{1,64}/g);
	if (!lines) throw new Error('Invalid login public key.');

	return ['-----BEGIN PUBLIC KEY-----', ...lines, '-----END PUBLIC KEY-----'].join('\n');
};

export const encryptRawPassword = (rawPassword: string, publicKey: string): string => {
	if (!rawPassword) throw new Error('Raw password is empty.');

	const encryptor = new JSEncrypt();
	encryptor.setPublicKey(convertPublicKeyToPem(publicKey));
	const encryptedPassword = encryptor.encrypt(rawPassword);
	if (!encryptedPassword) throw new Error('RSA password encryption failed.');

	return encryptedPassword;
};
